import './marketing.css'

import { Navigate, Outlet, useLocation } from 'react-router'

import leftSideTruck from '@/assets/marketing/images/left-side-truck-parking.svg'
import rightSideTruck from '@/assets/marketing/images/right-side-truck-parking.svg'
import { PageLoader } from '@/components/ui/PageLoader'
import { appHref } from '@/config/config'
import { trackMarketingCta } from '@/lib/analytics'
import { withCrossDomainIds } from '@/lib/crossDomainTracking'
import { useHostGuard } from '@/lib/hostGuard'
import { cn } from '@/lib/utils'
import { EdgeLineShine } from './components/EdgeLineShine'
import { SiteFooter } from './components/SiteFooter'
import { SiteNav } from './components/SiteNav'
import { ESTIMATE_BANNER_COPY } from './content/estimateBannerCopy'
import {
  SITE_FOOTER_LEGAL_LINKS,
  SITE_FOOTER_LINK_COLUMNS,
  SITE_FOOTER_NOTE,
  SITE_FOOTER_TAGLINE,
} from './content/siteFooterContent'

/* Decorative margin art — only shown once the gutter beside the 1200px content
   column is wide enough to fit it without crowding the edge line. */
const SIDE_GRAPHIC_CLASSES =
  'pointer-events-none fixed top-1/2 z-5 hidden h-auto max-h-[70vh] w-[calc((100%-var(--maxw))/2-40px)] max-w-50 -translate-y-1/2 min-[1600px]:block'

/**
 * Reflective-tape edge line beside the content column. The `after:` band is
 * the scroll-driven glint — EdgeLineShine keeps `--shine-y` in sync.
 */
function EdgeLine({ side }: { side: 'left' | 'right' }) {
  return (
    <div
      aria-hidden="true"
      className={cn(
        "pointer-events-none fixed inset-y-0 z-9999 w-1.75 overflow-hidden bg-[linear-gradient(to_right,rgba(255,255,255,0.9)_0,rgba(255,255,255,0.9)_1px,transparent_1px,transparent_3px,rgba(255,255,255,0.18)_3px,rgba(255,255,255,0.18)_7px)] after:absolute after:top-(--shine-y,-160px) after:right-0 after:left-0.75 after:h-(--shine-height,160px) after:bg-[linear-gradient(to_bottom,rgba(255,255,255,0)_0%,rgba(255,255,255,0.4)_50%,rgba(255,255,255,0)_100%)] after:content-['']",
        side === 'left'
          ? 'left-[max(0px,calc((100%-var(--maxw))/2-24px))]'
          : 'right-[max(0px,calc((100%-var(--maxw))/2-24px))]'
      )}
    />
  )
}

/**
 * Layout shared by every marketing route (home, insurance, privacy, terms):
 * the branded chrome (edge lines + side graphics + page loader), the sticky
 * nav, and the footer with its permanent estimate CTA. The whole tree mounts
 * inside `.marketing-root`, which exists only to scope the marketing `.reveal`
 * variant (design tokens are global, in css/index.css).
 */
export function MarketingLayout() {
  // In prod/staging these routes belong on the marketing host; if loaded on the
  // app host, bounce there (no-op in dev/preview).
  const { redirecting, navigateTo } = useHostGuard('marketing')
  const { pathname } = useLocation()
  // The trucks are solid white fills — barely visible against the site's usual
  // light backgrounds, but this page has a full-bleed black section that makes
  // them glare. Dim them just on this route rather than changing the shared art.
  const sideGraphicClassName = cn(SIDE_GRAPHIC_CLASSES, pathname === '/how-we-use-ai' && 'opacity-20')
  if (navigateTo) return <Navigate to={navigateTo} replace />
  if (redirecting) return null

  // shrink-0: the app shell (#root) is a flex column, so without it this flex
  // child shrinks to its min-height (100vh) and the grey bg stops mid-page while
  // taller content overflows below it.
  return (
    <div className="marketing-root bg-ink-50 min-h-screen shrink-0">
      <PageLoader />
      <EdgeLineShine />
      <EdgeLine side="left" />
      <EdgeLine side="right" />
      <img src={leftSideTruck} alt="" aria-hidden="true" className={cn(sideGraphicClassName, 'left-0')} />
      <img src={rightSideTruck} alt="" aria-hidden="true" className={cn(sideGraphicClassName, 'right-0')} />
      <SiteNav />
      <main>
        <Outlet />
      </main>
      <SiteFooter
        tagline={SITE_FOOTER_TAGLINE}
        linkColumns={SITE_FOOTER_LINK_COLUMNS}
        legalLinks={SITE_FOOTER_LEGAL_LINKS}
        note={SITE_FOOTER_NOTE}
        estimateBanner={{
          description: ESTIMATE_BANNER_COPY.description,
          buttonLabel: ESTIMATE_BANNER_COPY.buttonLabel,
          buttonLabelMobile: ESTIMATE_BANNER_COPY.buttonLabelMobile,
          onSubmit: (dot) => {
            trackMarketingCta('estimate_banner_footer', {
              dot_provided: Boolean(dot),
              dot_number: dot || undefined,
            })
            window.location.href = withCrossDomainIds(appHref(`/insurance/start${dot ? `?dot=${dot}` : ''}`))
          },
        }}
      />
    </div>
  )
}
